/**
 * Brand Kit (Round 56) — what Tourmaline knows about GEMS and what it may use.
 *   • the brief: who GEMS is, the voice, do / never, taglines, hashtags, notes
 *   • the facts it reads live from the product (shown, not edited)
 *   • the files: logo, photos, screenshots, clips, flyers, a font — uploaded
 *     here, kept on the server, used by the Studio and the daily cards
 *   • what it still needs, in plain words
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../../config';
import { toast } from 'sonner';
import { Upload, Trash2, RotateCcw, Save, Search, Film, Image as ImageIcon, Type, AlertTriangle, BookOpen, Eye } from 'lucide-react';

const BASE = '/api/admin/workforce/tourmaline';
const errText = (e, fallback) => e?.response?.data?.detail || fallback;
const KIND_ICON = { clip: Film, font: Type };

const asLines = (arr) => (arr || []).join('\n');
const fromLines = (text) => String(text || '').split('\n').map((s) => s.trim()).filter(Boolean);

const PeoplePicker = ({ api, value, onChange }) => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const seq = useRef(0);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRows([]); return undefined; }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      try { const r = await api.get(`${BASE}/members`, { params: { q: term } }); if (mine === seq.current) setRows(r.data.members || []); } catch (e) { /* stays empty */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q, api]);
  return (
    <div className="text-xs">
      <div className="flex flex-wrap gap-1 mb-1">
        {value.map((m) => (
          <span key={m.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-800">
            @{m.handle}{m.clip_release_signed ? '' : <span className="text-amber-700" title="No clip release on file — a post with this picture waits until it is recorded">· no release</span>}
            <button type="button" onClick={() => onChange(value.filter((x) => x.id !== m.id))} className="text-gray-400 hover:text-gray-700">×</button>
          </span>
        ))}
      </div>
      <div className="relative">
        <div className="flex items-center border rounded-lg px-2 bg-white"><Search className="w-3 h-3 text-gray-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Who is in the picture? (name or @handle)" className="flex-1 px-2 py-1 outline-none bg-transparent" /></div>
        {rows.length > 0 && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {rows.map((m) => (
              <button type="button" key={m.id} onClick={() => { if (!value.some((x) => x.id === m.id)) onChange([...value, m]); setQ(''); setRows([]); }} className="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center gap-2">
                <span className="font-medium text-gray-900">{m.name}</span><span className="text-gray-400">@{m.handle}</span>{m.clip_release_signed && <span className="ml-auto text-emerald-700">release on file</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const AssetThumb = ({ asset, size = 'w-full h-28' }) => {
  const Icon = KIND_ICON[asset.kind];
  if (asset.kind === 'clip') return <video src={`${API_BASE_URL}${asset.url}`} muted preload="metadata" className={`${size} rounded-lg bg-black object-cover border`} />;
  if (asset.kind === 'font') return <div className={`${size} rounded-lg border bg-gray-50 flex items-center justify-center text-2xl font-bold text-gray-700`}>Aa</div>;
  if (Icon) return <div className={`${size} rounded-lg border bg-gray-50 flex items-center justify-center`}><Icon className="w-6 h-6 text-gray-400" /></div>;
  return <img src={`${API_BASE_URL}${asset.url}`} alt="" loading="lazy" className={`${size} rounded-lg object-cover border bg-gray-100`} />;
};

const BrandKitPanel = ({ api, onChanged }) => {
  const [kit, setKit] = useState(null);
  const [brief, setBrief] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const [kind, setKind] = useState('photo');
  const [tags, setTags] = useState('');
  const [people, setPeople] = useState([]);
  const [files, setFiles] = useState([]);
  const [game, setGame] = useState('');
  const [scene, setScene] = useState('');
  const [showBin, setShowBin] = useState(false);
  const [showFacts, setShowFacts] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await api.get(`${BASE}/kit`); setKit(r.data); setBrief(r.data.brief); setDirty(false); } catch (e) { toast.error('Could not load the Brand Kit'); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const call = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); if (ok) toast.success(typeof ok === 'function' ? ok(r) : ok); await load(); if (onChanged) onChanged(); return r; }
    catch (e) { toast.error(errText(e, 'That did not work'), { duration: 8000 }); return null; }
    finally { setBusy(''); }
  };

  if (!kit || !brief) return <div className="p-4 text-sm text-gray-500">Loading the Brand Kit…</div>;

  const setB = (k, v) => { setBrief((b) => ({ ...b, [k]: v })); setDirty(true); };
  const upload = async () => {
    if (!files.length) { toast.error('Choose the files first'); return; }
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    form.append('kind', kind); form.append('tags', tags); form.append('featured_user_ids', people.map((m) => m.id).join(','));
    form.append('game', game); form.append('scene', scene);
    const r = await call('upload', () => api.post(`${BASE}/kit/assets`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
      (res) => `${res.data.added.length} added${res.data.errors.length ? ` · ${res.data.errors.length} refused` : ''}`);
    if (r) {
      (r.data.errors || []).forEach((e) => toast.error(`${e.file}: ${e.error}`, { duration: 10000 }));
      setFiles([]); setTags(''); setPeople([]); if (fileRef.current) fileRef.current.value = '';
    }
  };
  const groups = ['logo', 'photo', 'screenshot', 'clip', 'flyer', 'font'].map((k) => [k, kit.assets.filter((a) => a.kind === k)]).filter(([, rows]) => rows.length);

  return (
    <div className="space-y-6" data-testid="brand-kit">
      {kit.needs.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Tourmaline still needs</p>
          <ul className="mt-1 space-y-1">
            {kit.needs.map((n) => <li key={n.what}><b>{n.what}.</b> {n.why}. <span className="text-amber-800">{n.how}</span></li>)}
          </ul>
        </div>
      )}

      {kit.vision && kit.assets.some((a) => ['photo', 'screenshot', 'flyer'].includes(a.kind) && !a.seen) && (
        <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900 flex flex-wrap items-center gap-3" data-testid="kit-unseen">
          <span><b>{kit.assets.filter((a) => ['photo', 'screenshot', 'flyer'].includes(a.kind) && !a.seen).length} picture(s) have not been looked at yet.</b> Until they are, the daily cards only put game-free lines on them.</span>
          <button onClick={() => call('lookall', () => api.post(`${BASE}/kit/look-all`), (r) => `${r.data.seen} looked at · ${r.data.skipped} could not · ${r.data.left} left`)} disabled={busy === 'lookall'} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 disabled:opacity-50" data-testid="kit-look-all">
            <Eye className="w-3.5 h-3.5" /> {busy === 'lookall' ? 'Looking… (about five seconds each)' : 'Look at all of them now'}
          </button>
        </div>
      )}
      {!kit.vision && (
        <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900" data-testid="kit-no-vision">
          <b>Tourmaline cannot look at the pictures yet.</b> With a Claude key on the server (<code>ANTHROPIC_API_KEY</code>, typed by the owner into the host's settings) it reads every picture — the game on it, what the people are doing, where the faces are — and writes lines that fit. Until then, choose the game and the scene for each picture below; the daily cards and the Studio match on those.
        </div>
      )}

      {/* Upload */}
      <section className="bg-white border rounded-xl p-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Upload className="w-4 h-4" /> Add to the kit</h3>
        <p className="text-xs text-gray-500 mt-1">Photos and screenshots from lives (JPEG, PNG, WebP up to {kit.limits.image_mb} MB, at least {kit.limits.min_photo_px} px), clips (MP4 up to {kit.limits.clip_mb} MB and {kit.limits.clip_seconds} seconds), the logo (PNG, transparent), a font (TTF). Several files at once.</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="text-sm text-gray-700">What are these?
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-full bg-white" data-testid="kit-kind">
              {Object.entries(kit.kinds).map(([k, label]) => <option key={k} value={k}>{label}{k === 'photo' ? ' — people at the table, stills from lives' : k === 'screenshot' ? ' — the product on a phone or the web' : ''}</option>)}
            </select>
          </label>
          <label className="text-sm text-gray-700">Tags (optional, comma-separated)
            <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="spades, friday night, mobile" className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-full" />
          </label>
          <label className="text-sm text-gray-700 sm:col-span-2">Files
            <input ref={fileRef} type="file" multiple accept={kind === 'clip' ? 'video/mp4,video/quicktime' : kind === 'font' ? '.ttf,.otf,font/ttf' : 'image/jpeg,image/png,image/webp'} onChange={(e) => setFiles(Array.from(e.target.files || []))} className="mt-1 block text-sm" data-testid="kit-files" />
          </label>
          {['photo', 'screenshot', 'flyer'].includes(kind) && (
            <>
              <label className="text-sm text-gray-700">Game on it (optional — Tourmaline sees it when it has a key)
                <select value={game} onChange={(e) => setGame(e.target.value)} className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-full bg-white" data-testid="kit-game">
                  <option value="">Let Tourmaline decide</option>
                  {Object.entries(kit.games || {}).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              </label>
              <label className="text-sm text-gray-700">What is happening (optional)
                <select value={scene} onChange={(e) => setScene(e.target.value)} className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-full bg-white" data-testid="kit-scene">
                  <option value="">Let Tourmaline decide</option>
                  {Object.entries(kit.scenes || {}).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              </label>
            </>
          )}
          {['photo', 'screenshot', 'clip', 'flyer'].includes(kind) && (
            <div className="sm:col-span-2">
              <p className="text-sm text-gray-700 mb-1">Members shown (so their clip release is checked before a post uses it)</p>
              <PeoplePicker api={api} value={people} onChange={setPeople} />
            </div>
          )}
        </div>
        <button onClick={upload} disabled={busy === 'upload' || !files.length} className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold disabled:opacity-40" data-testid="kit-upload">
          <Upload className="w-4 h-4" /> {busy === 'upload' ? 'Uploading…' : `Upload ${files.length || ''}`.trim()}
        </button>
      </section>

      {/* Files */}
      <section>
        <h3 className="font-semibold text-gray-900 mb-2">In the kit <span className="text-gray-400 font-normal">({kit.assets.length})</span></h3>
        {kit.assets.length === 0 && <p className="text-sm text-gray-500 border rounded-xl px-4 py-6 text-center bg-white">Nothing yet. Upload the logo, a few photos from lives and a couple of screenshots — the Studio builds everything from here.</p>}
        {groups.map(([k, rows]) => (
          <div key={k} className="mb-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{kit.kinds[k]}s · {rows.length}</p>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
              {rows.map((a) => (
                <div key={a.id} className="bg-white border rounded-xl p-2 text-xs" data-testid={`kit-asset-${a.id}`}>
                  <AssetThumb asset={a} />
                  <input defaultValue={a.title} onBlur={(e) => e.target.value.trim() !== a.title && call(`t:${a.id}`, () => api.post(`${BASE}/kit/assets/${a.id}`, { title: e.target.value }), 'Saved')} className="mt-1 w-full font-medium text-gray-900 bg-transparent outline-none border-b border-transparent focus:border-gray-300" title="Click to rename" />
                  <p className="text-gray-400 truncate">{a.width ? `${a.width}×${a.height}` : ''}{a.duration ? ` · ${Math.round(a.duration)}s` : ''}{a.uses ? ` · used ${a.uses}×` : ''}{(a.featured_user_ids || []).length ? ` · ${a.featured_user_ids.length} member${a.featured_user_ids.length === 1 ? '' : 's'}` : ''}</p>
                  {(a.tags || []).length > 0 && <p className="text-gray-400 truncate">#{a.tags.join(' #')}</p>}
                  {['photo', 'screenshot', 'flyer'].includes(a.kind) && (
                    <div className="mt-1 space-y-1" data-testid={`kit-seen-${a.id}`}>
                      {a.seen ? <p className="text-gray-600 line-clamp-2" title={a.seen.description}>{a.seen.description}{a.kind_note ? ` (${a.kind_note})` : ''}</p>
                        : <p className="text-gray-400 italic">{kit.vision ? 'Not looked at yet' : 'Not seen — set the game and scene'}</p>}
                      <div className="flex gap-1">
                        <select value={a.kind} onChange={(e) => call(`k:${a.id}`, () => api.post(`${BASE}/kit/assets/${a.id}`, { kind: e.target.value }), 'Re-filed')} className="border rounded px-1 py-0.5 text-[11px] bg-white flex-1 min-w-0" title="What it is — a screenshot is shown whole, a photo fills the card">
                          <option value="photo">Photo</option><option value="screenshot">Screenshot</option><option value="flyer">Flyer</option>
                        </select>
                        <select value={a.game || ''} onChange={(e) => call(`g:${a.id}`, () => api.post(`${BASE}/kit/assets/${a.id}`, { game: e.target.value }), 'Saved')} className="border rounded px-1 py-0.5 text-[11px] bg-white flex-1 min-w-0" title="The game on it">
                          <option value="">game?</option>
                          {Object.entries(kit.games || {}).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                        </select>
                        <select value={a.scene || ''} onChange={(e) => call(`s:${a.id}`, () => api.post(`${BASE}/kit/assets/${a.id}`, { scene: e.target.value }), 'Saved')} className="border rounded px-1 py-0.5 text-[11px] bg-white flex-1 min-w-0" title="What is happening">
                          <option value="">scene?</option>
                          {Object.entries(kit.scenes || {}).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                        </select>
                      </div>
                      {kit.vision && <button onClick={() => call(`look:${a.id}`, () => api.post(`${BASE}/kit/assets/${a.id}/look`), (r) => `Seen: ${r.data.seen.description}`)} disabled={busy === `look:${a.id}`} className="inline-flex items-center gap-1 text-violet-700 hover:text-violet-900 disabled:opacity-50"><Eye className="w-3 h-3" /> {busy === `look:${a.id}` ? 'Looking…' : a.seen ? 'Look again' : 'Look now'}</button>}
                    </div>
                  )}
                  <button onClick={() => call(`rm:${a.id}`, () => api.post(`${BASE}/kit/assets/${a.id}/remove`), 'Moved to the bin')} className="mt-1 inline-flex items-center gap-1 text-gray-500 hover:text-red-700"><Trash2 className="w-3 h-3" /> Remove</button>
                </div>
              ))}
            </div>
          </div>
        ))}
        {kit.bin.length > 0 && (
          <div className="mt-2 text-sm">
            <button onClick={() => setShowBin((s) => !s)} className="text-gray-500 underline">{showBin ? 'Hide' : 'Show'} the bin ({kit.bin.length})</button>
            {showBin && (
              <div className="mt-2 bg-white border rounded-xl divide-y">
                {kit.bin.map((a) => (
                  <div key={a.id} className="px-3 py-2 flex items-center gap-3 text-xs">
                    <AssetThumb asset={a} size="w-10 h-10" /><span className="font-medium text-gray-800 flex-1 truncate">{a.title}</span>
                    <button onClick={() => call(`rs:${a.id}`, () => api.post(`${BASE}/kit/assets/${a.id}/restore`), 'Restored')} className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900"><RotateCcw className="w-3 h-3" /> Restore</button>
                  </div>
                ))}
                <div className="px-3 py-2"><button onClick={() => window.confirm('Delete every file in the bin for good?') && call('bin', () => api.post(`${BASE}/kit/bin/empty`), (r) => `${r.data.deleted} file(s) deleted`)} className="text-xs text-red-700">Empty the bin</button></div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* The brief */}
      <section className="bg-white border rounded-xl p-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2"><BookOpen className="w-4 h-4" /> The brief — who GEMS is, how it talks</h3>
        <p className="text-xs text-gray-500 mt-1">Every caption, idea and picture is written from this and from the facts below. Lapis reads every change.</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {[['about', 'About GEMS'], ['audience', 'Who we talk to'], ['voice', 'The voice']].map(([k, label]) => (
            <label key={k} className="text-sm text-gray-700 sm:col-span-2">{label}
              <textarea value={brief[k]} onChange={(e) => setB(k, e.target.value)} rows={3} maxLength={1200} className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-full" data-testid={`brief-${k}`} />
            </label>
          ))}
          <label className="text-sm text-gray-700">Always (one per line)
            <textarea value={asLines(brief.do)} onChange={(e) => setB('do', fromLines(e.target.value))} rows={6} className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-full" />
          </label>
          <label className="text-sm text-gray-700">Never (one per line)
            <textarea value={asLines(brief.dont)} onChange={(e) => setB('dont', fromLines(e.target.value))} rows={6} className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-full" />
          </label>
          <label className="text-sm text-gray-700">Taglines (one per line)
            <textarea value={asLines(brief.taglines)} onChange={(e) => setB('taglines', fromLines(e.target.value))} rows={4} className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-full" />
          </label>
          <div className="space-y-3">
            <label className="text-sm text-gray-700 block">Hashtags
              <input value={brief.hashtags} onChange={(e) => setB('hashtags', e.target.value)} className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-full" />
            </label>
            <label className="text-sm text-gray-700 block">Notes from you (what is on this month, what to push)
              <textarea value={brief.notes} onChange={(e) => setB('notes', e.target.value)} rows={3} className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-full" data-testid="brief-notes" />
            </label>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button disabled={!dirty || busy === 'brief'} onClick={() => call('brief', () => api.post(`${BASE}/kit/brief`, brief), 'Brief saved')} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold disabled:opacity-40" data-testid="brief-save"><Save className="w-4 h-4" /> Save the brief</button>
          <button onClick={() => { setBrief({ ...kit.defaults }); setDirty(true); }} className="text-sm text-gray-500 underline">Back to the defaults</button>
        </div>
      </section>

      {/* What it knows */}
      <section className="bg-white border rounded-xl p-4">
        <button onClick={() => setShowFacts((s) => !s)} className="font-semibold text-gray-900 flex items-center gap-2"><ImageIcon className="w-4 h-4" /> What Tourmaline knows right now <span className="text-xs font-normal text-gray-400">{showFacts ? 'hide' : 'show'}</span></button>
        {showFacts && <pre className="mt-2 text-[11px] whitespace-pre-wrap bg-gray-50 border rounded-lg p-3 text-gray-700 max-h-96 overflow-y-auto" data-testid="kit-knowledge">{kit.knowledge}</pre>}
        {!showFacts && <p className="text-xs text-gray-500 mt-1">The brief above plus the live facts: the games, GEMS Plus and its price, the currencies and the wording rules, tournament levels, the weekly shows, the links. Read from the product itself — nothing to keep in sync.</p>}
      </section>
    </div>
  );
};

export default BrandKitPanel;
